import { newLocalId } from '@/lib/protocol-ids';
import type { RuleRow } from '@/lib/rules-settings';
import type {
  ExitCondition,
  ProtocolObjectiveDraft,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import {
  CRITERION_EXAMPLES_MAX,
  GLOBAL_RULES_BLOCK_MAX,
  PROTOCOL_CONDITIONS_MAX,
  PROTOCOL_OBJECTIVES_MAX,
  PROTOCOL_OBJECTIVE_NAME_MAX,
  PROTOCOL_STEPS_MAX,
  PROTOCOL_STEP_NAME_MAX,
  cloneExitCondition,
  cloneProtocolObjective,
  cloneProtocolStep,
  nextFreeCopyName,
} from '@matter_extractor/shared';

/**
 * « Dupliquer » for the FIVE items a screen of this package offers it on
 * (refonte modulaire §4.2, « un item en page »; issue #117): a protocol step,
 * an objective, an exit condition, a criterion example, a global rule.
 *
 * Pure, so the five gestures are tested without a render, and gathered in ONE
 * file although they belong to three different screens. What they share is not
 * a data shape - a step is a tree, an example is a `string` - but the three
 * rules §4.2 states once for all of them:
 *
 *   1. new ids inside the copied subtree, references readdressed inside and
 *      left alone outside (`shared/duplication`, issue #115);
 *   2. the copy lands JUST AFTER its source, never at the end of the list;
 *   3. at the list's cap, the gesture refuses with a SENTENCE and not a
 *      silence, because « un geste sans boîte n'a que cet endroit-là pour
 *      refuser ».
 *
 * Written five times across three screens, rule 2 would have drifted the first
 * time somebody reached for `push`.
 *
 * ## What is NOT here, and why
 *
 * The CLONING of a step, an objective and a condition is
 * `@matter_extractor/shared` and stays there: it is the part the backend's own
 * checks are written against, and it has no business being spelled twice. What
 * this file adds around it is what a subtree cannot know - the list it lives
 * in, therefore the cap, the position and the names already taken.
 *
 * An EXAMPLE and a RULE have no clone at all, on purpose (`shared`'s
 * `clone.ts` says why at length): an example is a bare `string`, and a rule row
 * whose `id` is ABSENT already means « create this one » to `PUT /api/rules`
 * (§11.3). Both are therefore duplicated by copying the value, and their
 * functions below are the insertion and nothing else.
 *
 * ## The name, and the two items that take one
 *
 * « (copie) » goes on the STEP and the OBJECTIVE only - §4.2: « rien sur ceux
 * qui n'en portent pas, qui n'ont qu'un texte ou un numéro ». The suffix comes
 * from `nextFreeCopyName`, fed with the SIBLINGS' names: the names of a
 * protocol's steps for a step, the names of one step's objectives for an
 * objective. Homonyms are legal (§7.2), so this is not a unicity check - it is
 * what makes « Ancrage (copie) » become « Ancrage (copie 2) » when the first
 * copy is still sitting there unedited, which is the whole point of the
 * suffix: « un jumeau non édité dans la palette @ est un piège ».
 *
 * A step or an objective whose name is EMPTY - the placeholder state, where the
 * screen shows « Étape 3 » without anybody having typed it - copies with an
 * empty name, because `nextFreeCopyName` answers `''` on a blank source. That
 * is right: « Étape 3 (copie) » would put a rank nobody chose into a field
 * somebody is about to fill.
 */

/* ------------------------------------------------------------- the refusals */

/**
 * Why the copy icon is closed, in French, or `null` when it is open.
 *
 * A sentence and not a boolean, for the reason `rulesBlocked` gives next door:
 * §4.2 asks the icon to be « désactivée et dire pourquoi au survol », and a
 * control that greys without a word is a control an operator clicks three times
 * before looking for the rule.
 */
function capReason(count: number, max: number, plural: string): string | null {
  return count < max ? null : `Plafond atteint : ${max} ${plural} au plus.`;
}

export function stepDuplicationBlocked(steps: readonly ProtocolStepDraft[]): string | null {
  return capReason(steps.length, PROTOCOL_STEPS_MAX, 'étapes');
}

export function objectiveDuplicationBlocked(
  objectives: readonly ProtocolObjectiveDraft[],
): string | null {
  return capReason(objectives.length, PROTOCOL_OBJECTIVES_MAX, 'objectifs');
}

export function conditionDuplicationBlocked(conditions: readonly ExitCondition[]): string | null {
  return capReason(conditions.length, PROTOCOL_CONDITIONS_MAX, 'conditions de sortie');
}

export function exampleDuplicationBlocked(examples: readonly string[]): string | null {
  return capReason(examples.length, CRITERION_EXAMPLES_MAX, 'exemples');
}

export function ruleDuplicationBlocked(rows: readonly RuleRow[]): string | null {
  return capReason(rows.length, GLOBAL_RULES_BLOCK_MAX, 'règles');
}

/* ------------------------------------------------------------ the insertion */

/**
 * The copy, JUST AFTER its source (§4.2), and the one spelling of that rule.
 *
 * `index` is the source's own index, so the copy goes at `index + 1` and every
 * later element shifts by one. For a global rule that shift IS a demotion of
 * one priority rank, which §11.3 makes correct rather than surprising: « l'ordre
 * du tableau est l'ordre des priorités ».
 */
function insertAfter<Item>(list: readonly Item[], index: number, copy: Item): Item[] {
  return [...list.slice(0, index + 1), copy, ...list.slice(index + 1)];
}

/** What a duplication answers: the next list, and the copy to select. */
export type Duplicated<Item> = {
  readonly list: Item[];
  /** The copy's id, for the selection §4.2 asks for right after the gesture. */
  readonly cloneId: string;
};

/**
 * `null` when the gesture must not happen: no such source, or the list is full.
 *
 * The screens close the icon before it can be clicked, so this second refusal
 * is never what an operator meets. It is here because a pure function that
 * silently overflows a cap is a pure function that will overflow it the day a
 * caller forgets the icon - and `protocolContentSchema` would then refuse the
 * save with a message about a bound rather than about the gesture.
 */
export function duplicateStep(
  steps: readonly ProtocolStepDraft[],
  stepId: string,
): Duplicated<ProtocolStepDraft> | null {
  const index = steps.findIndex((step) => step.id === stepId);
  const source = steps[index];
  if (source === undefined || stepDuplicationBlocked(steps) !== null) return null;

  const { clone } = cloneProtocolStep(source, newLocalId);
  const named: ProtocolStepDraft = {
    ...clone,
    name: nextFreeCopyName(
      source.name,
      steps.map((step) => step.name),
      PROTOCOL_STEP_NAME_MAX,
    ),
  };
  return { list: insertAfter(steps, index, named), cloneId: named.id };
}

/**
 * One objective, duplicated inside its step.
 *
 * The clone readdresses an autocitation - a direction quoting the objective's
 * OWN `{{objectif:...}}` - towards the copy, and leaves a quote of a SIBLING
 * objective alone, because the sibling is not copied. That is the general rule
 * of §4.2 rather than a branch, and the sentence of §4.2 which said an
 * objective's readdressing table is always empty is corrected in this same lot.
 */
export function duplicateObjective(
  objectives: readonly ProtocolObjectiveDraft[],
  objectiveId: string,
): Duplicated<ProtocolObjectiveDraft> | null {
  const index = objectives.findIndex((objective) => objective.id === objectiveId);
  const source = objectives[index];
  if (source === undefined || objectiveDuplicationBlocked(objectives) !== null) return null;

  const { clone } = cloneProtocolObjective(source, newLocalId);
  const named: ProtocolObjectiveDraft = {
    ...clone,
    name: nextFreeCopyName(
      source.name,
      objectives.map((objective) => objective.name),
      PROTOCOL_OBJECTIVE_NAME_MAX,
    ),
  };
  return { list: insertAfter(objectives, index, named), cloneId: named.id };
}

/**
 * One exit condition, duplicated inside its step - verbatim but for its id.
 *
 * No name: a condition is read by its NUMBER on the card, and the numbering
 * simply follows the insertion (§8.4, « les cartes sont numérotées »).
 */
export function duplicateCondition(
  conditions: readonly ExitCondition[],
  conditionId: string,
): Duplicated<ExitCondition> | null {
  const index = conditions.findIndex((condition) => condition.id === conditionId);
  const source = conditions[index];
  if (source === undefined || conditionDuplicationBlocked(conditions) !== null) return null;

  const { clone } = cloneExitCondition(source, newLocalId);
  return { list: insertAfter(conditions, index, clone), cloneId: clone.id };
}

/**
 * One criterion example, duplicated verbatim (§6).
 *
 * A bare `string` with no id and no name, so there is nothing to mint and
 * nothing to suffix - the list is POSITIONAL, and the copy's « numéro intégré à
 * gauche » is just its new index. It answers the list alone, because an example
 * has no id to select afterwards.
 */
export function duplicateExample(examples: readonly string[], index: number): string[] | null {
  const source = examples[index];
  if (source === undefined || exampleDuplicationBlocked(examples) !== null) return null;
  return insertAfter(examples, index, source);
}

/**
 * One global rule, duplicated as a row of the DRAFT of « Les règles » (§11.3,
 * issue #192).
 *
 * The copy carries an identifier of its own, minted here: a rule keeps its id
 * across versions, so what a turn recorded about it still names it, and the copy
 * must not share the source's. The rules below it come back one priority lower,
 * which is the visible and correct consequence of the array being the order.
 *
 * The id is MINTED here rather than taken as a counter from the caller, and that
 * is not a detail: a counter read at render time is the same number for two
 * duplications resolved against one render, and two rows sharing a React key
 * make React reuse the wrong row's DOM - which, on a list of textareas, shows
 * up as a caret jumping to another rule. A random id is exactly as good as a
 * key and cannot collide.
 */
export function duplicateRuleRow(rows: readonly RuleRow[], index: number): RuleRow[] | null {
  const source = rows[index];
  if (source === undefined || ruleDuplicationBlocked(rows) !== null) return null;
  return insertAfter(rows, index, { id: newLocalId(), text: source.text });
}
